import React, { useEffect, useState, useRef, useCallback } from 'react';
import '../styles/main.scss';
import './FeedPage.scss';
import PostCard from '../components/PostCard';
import { getPosts } from '../api/api';
import CreatePostPage from './CreatePostPage';
import { supabase } from '../lib/supabase';
import { Post } from '../api/api';

const LIMIT = 10;

const FeedPage: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [filterType, setFilterType] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const observer = useRef<IntersectionObserver | null>(null);

  //ПОИСК
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    const channel = supabase
      .channel('posts-realtime')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'posts',
        },
        (payload) => {
          const newPost = payload.new as Post;

          setPosts(prev => {
            if (prev.some(p => p.id === newPost.id)) return prev;

            return [newPost, ...prev];
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadPosts = useCallback(
    async (pageToLoad: number, reset = false) => {
      setLoading(true);

      try {
        const newPosts = await getPosts(
          pageToLoad,
          LIMIT,
          filterType || undefined,
          debouncedSearch || undefined
        );

        setPosts(prev => {
          if (reset) return newPosts;

          const existingIds = new Set(prev.map(p => p.id));
          const filtered = newPosts.filter(p => !existingIds.has(p.id));

          return [...prev, ...filtered];
        });

        setHasMore(newPosts.length === LIMIT);
      } finally {
        setLoading(false);
      }
    },
    [filterType, debouncedSearch]
  );

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    loadPosts(0, true);
  }, [filterType, debouncedSearch, loadPosts]);

  useEffect(() => {
    if (page === 0) return;
    loadPosts(page);
  }, [page, loadPosts]);


  const lastPostRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (loading) return;

      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          setPage(prev => prev + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const sortPosts = (type: string | null) => {
    setFilterType(type);
  };

  const handleDeletePost = (id: number) => {
    setPosts(prev => prev.filter(p => p.id !== id));
  };

  const handleRefresh = () => {
    setPosts([]);
    setPage(0);
    setHasMore(true);
    loadPosts(0, true);
  };

  return (
    <div className="feed-page">
      <button
        className="button-neon toggle-new-btn"
        onClick={() => setIsOpen(prev => !prev)}
      >
        {isOpen ? 'Закрыть' : 'Новый пост'}
      </button>

      {isOpen && (
        <CreatePostPage
          onCreated={() => {
            setIsOpen(false);
            setPage(0);
            loadPosts(0, true);
          }}
        />
      )}

      <div className="category-selector --hidden">
        <button className={!filterType ? 'active' : ''} onClick={() => sortPosts(null)}>
          🌍 Все
        </button>
        <button className={filterType === 'music' ? 'active' : ''} onClick={() => sortPosts('music')}>
          🎵 Музыка
        </button>
        <button className={filterType === 'thoughts' ? 'active' : ''} onClick={() => sortPosts('thoughts')}>
          ✍️ Мысли
        </button>
        <button className={filterType === 'video' ? 'active' : ''} onClick={() => sortPosts('video')}>
          🎬 Видео
        </button>
      </div>

      <div className="search-box flex">
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="🔍 Поиск постов..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button
          className="refresh-btn"
          onClick={handleRefresh}
          disabled={loading}
        >
          {loading ? 'Загрузка...' : '🔄 Обновить'}
        </button>
      </div>

      <div className="posts">

        {posts.map((p, i) => {
          const isLast = i === posts.length - 1;

          return (
            <PostCard
              key={p.id}
              {...p}
              onDeletePost={handleDeletePost}
              ref={isLast ? lastPostRef : null}
            />
          );
        })}
      </div>

      <div className='footer flex'>
        {loading && <p style={{ margin: 20, alignSelf: 'center' }}>Загрузка...</p>}
        {!hasMore && <p style={{ margin: 20 }}>Больше постов нет</p>}
      </div>
    </div>
  );
};

export default FeedPage;